import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, Pill, PageContainer, PageHeader } from '../../components';
import { formatDate, formatHours } from '../../lib/format';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { TYPE_LABELS, TYPE_TONES } from '../requests/labels';
import {
  completedQueryOptions,
  isInternalCompletedItem,
  projectOptionsQueryOptions,
  type CompletedItem,
} from './api';

/**
 * Completed — released work, newest first.
 *
 * This is the "what have we actually got for our money" screen, so it reads
 * as a record rather than a backlog: no drag handles, no rank, nothing to
 * act on — just what shipped, when, and under which project. Filters
 * narrow the record; they never change what counts as released.
 */

const ALL = '';

function CompletedRow({ item }: { readonly item: CompletedItem }) {
  const internal = isInternalCompletedItem(item) ? item : null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-line bg-base px-3.5 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="fs-11 whitespace-nowrap text-muted">#{item.number}</span>
          <Pill tone={TYPE_TONES[item.type]}>{TYPE_LABELS[item.type]}</Pill>
          {item.sprintName !== null && <Pill tone="neutral">{item.sprintName}</Pill>}
          {internal !== null && !internal.visibleToClient && <Pill tone="violet">Internal</Pill>}
        </div>
        <div className="mt-0.5 truncate fs-13.5 font-semibold text-ink">{item.title}</div>
        <div className="mt-0.5 truncate fs-11 text-muted">
          {item.projectName ?? 'No project'} · {formatHours(item.approxHours)}
          {internal !== null && ` · ${internal.ownerName ?? 'Unassigned'}`}
        </div>
      </div>
      <div className="flex-none text-right fs-11 whitespace-nowrap text-muted">
        {formatDate(item.closedAt ?? item.updatedAt)}
      </div>
    </div>
  );
}

export function CompletedPage() {
  const { workspace } = useRequiredWorkspace();
  const [projectId, setProjectId] = useState(ALL);

  // Unfiltered, fetched once: this is what seeds the dropdown with every
  // project that has EVER shipped, regardless of whichever filter is currently
  // applied to the list below. TanStack dedupes this against the list query
  // itself whenever no filter is selected — same key, one request.
  //
  // There were two dropdowns here, project and area, and they filtered the
  // same list by the same thing under two names. See the `projects` table in
  // apps/api/src/db/schema/work.ts.
  const baselineQuery = useQuery(completedQueryOptions(workspace.id, {}));
  const projectsQuery = useQuery(projectOptionsQueryOptions(workspace.id));
  const itemsQuery = useQuery(
    completedQueryOptions(workspace.id, {
      projectId: projectId === ALL ? undefined : projectId,
    }),
  );

  // Only projects that have actually shipped something — an option for a
  // project with nothing released yet would filter the list down to empty
  // every time, which reads as a dead end rather than a filter.
  const projectOptions = useMemo(() => {
    const idsWithReleases = new Set<string>();
    for (const item of baselineQuery.data ?? []) {
      if (item.projectId !== null) idsWithReleases.add(item.projectId);
    }
    return (projectsQuery.data ?? []).filter((project) => idsWithReleases.has(project.id));
  }, [baselineQuery.data, projectsQuery.data]);

  const items = itemsQuery.data ?? [];
  const filtered = projectId !== ALL;

  return (
    <PageContainer>
      <PageHeader
        title="Completed"
        description="Released work, newest first — what has actually shipped."
        actions={
          <>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              aria-label="Filter by project"
              className="rounded-[8px] border border-line-strong bg-surface-2 px-2.5 py-1.5 fs-12 text-ink"
            >
              <option value={ALL}>All projects</option>
              {projectOptions.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </>
        }
      />

      {itemsQuery.isPending ? (
        <p className="mt-6 fs-12 text-muted">Loading…</p>
      ) : itemsQuery.error !== null ? (
        <EmptyState
          title="Could not load completed work"
          description={itemsQuery.error.message}
          className="mt-6"
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing released yet"
          description={
            filtered ? 'Nothing matches this filter yet.' : 'Work appears here once it reaches Done.'
          }
          className="mt-6"
        />
      ) : (
        <div className="mt-6 flex flex-col gap-2">
          {items.map((item) => (
            <CompletedRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
